import { styled, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PatternsLabels } from 'src/constants/pattern';
import { useUser } from 'src/context/UserProvider';
import { usePattern } from 'src/hook/usePattern';
import { WebGLRenderer } from 'three';
import * as THREE from 'three';

import { StateNames, States } from '../../../../Screens/Configurator';
import { EngravingItem } from './Engravings/EngravingItem';
import { KanvasEditor } from './KanvasEditor';
import { Catalog } from './Patterns/Catalog';
import { PatternItem } from './Patterns/PatternItem';

type ViewOption = {
  key: 'x' | 'y' | 'z' | 'free';
  inverse: boolean;
  label: string;
  icon: string;
};

const VIEW_OPTIONS: ViewOption[] = [
  {
    key: 'free',
    inverse: false,
    label: 'Vue globale',
    icon: '/faces/none.png',
  },
  // Ligne 1 : Avant, Gauche, Droite
  { key: 'z', inverse: false, label: 'Avant', icon: '/faces/front.png' },
  { key: 'x', inverse: false, label: 'Gauche', icon: '/faces/left.png' },
  { key: 'x', inverse: true, label: 'Droite', icon: '/faces/right.png' },
  // Ligne 2 : Arrière, Dessus, Dessous
  { key: 'z', inverse: true, label: 'Arrière', icon: '/faces/back.png' },
  { key: 'y', inverse: false, label: 'Dessus', icon: '/faces/up.png' },
  { key: 'y', inverse: true, label: 'Dessous', icon: '/faces/down.png' },
];

type PatternsProps = {
  pieces: any[];
  pieceIndex: number;
  setPatterns: (patterns: { [key: string]: { [key: string]: any }[] }) => void;
  patterns: {
    [key: string]: { [key: string]: any }[];
  };
  selectedPattern: number | undefined;
  setSelectedPattern: (selectedPattern: number | undefined) => void;
  orbitRef: React.MutableRefObject<any>;
  centerCamera: () => void;
  setValue: <T extends StateNames>(stateName: T, value: States[T]) => void;
  gl: WebGLRenderer | undefined;
  orientation: {
    key: 'x' | 'y' | 'z' | 'free';
    inverse: boolean;
  };
  setOrientation: (orientation: {
    key: 'x' | 'y' | 'z' | 'free';
    inverse: boolean;
  }) => void;
  selectedPage: string;
  customSteles: any[];
  customPlatings: any[];
  customSoles: any[];
};

export const PatternsEngraving = ({
  pieces,
  pieceIndex,
  setPatterns,
  patterns,
  setSelectedPattern,
  selectedPattern,
  orbitRef,
  centerCamera,
  setValue,
  gl,
  orientation,
  setOrientation,
  selectedPage,
  customPlatings,
  customSteles,
  customSoles,
}: PatternsProps) => {
  const shouldUpdateTexture = useRef<{
    pieceUUID: string;
    orientation: string;
  } | null>(null);

  const {
    kanvasSize,
    selectedImportPage,
    setSelectedImportPage,
    setSelectedPiece,
    isButtonOpen,
    setIsButtonOpen,
    selectedPiece,
    updateKanvas,
    changePattern,
    applyPatternsTmp,
    getPatterns,
    addText,
    changeText,
  } = usePattern({
    pieces,
    customSteles,
    customPlatings,
    pieceIndex,
    setPatterns,
    patterns,
    orbitRef,
    centerCamera,
    setValue,
    gl,
    orientation,
    setOrientation,
    shouldUpdateTexture,
    customSoles,
  });
  const { user } = useUser();

  const stageRef = useRef<any>();

  const [isPatternsLoaded, setIsPatternsLoaded] = useState<{
    [key: string]: boolean;
  }>({});

  const updateTexture = (pieceUUID: string, orientation: string) => {
    const patternsTmp = { ...patterns };

    if (
      stageRef.current &&
      patternsTmp[pieceUUID] &&
      patternsTmp[pieceUUID][orientation]
    ) {
      const canvas = stageRef.current.toCanvas();
      const texture = new THREE.CanvasTexture(canvas);
      patternsTmp[pieceUUID][orientation].texture = texture;
    }
    setPatterns(patternsTmp);
  };

  const isTexturesLoading = () => {
    const patterns = getPatterns();
    for (let i = 0; i < patterns.length; i += 1) {
      if (isPatternsLoaded[i]) return true;
    }
    return false;
  };

  useEffect(() => {
    setIsButtonOpen(false);
    setOrientation({ key: 'free', inverse: false });
  }, [selectedPage, setIsButtonOpen, setOrientation]);

  useEffect(() => {
    if (getPatterns().length > 0 && selectedPattern === undefined)
      setSelectedPattern(0);
  }, [getPatterns, orientation, selectedPattern, setSelectedPattern]);

  useEffect(() => {
    if (
      shouldUpdateTexture &&
      shouldUpdateTexture.current !== null &&
      shouldUpdateTexture.current.pieceUUID &&
      shouldUpdateTexture.current.orientation
    ) {
      const { pieceUUID } = shouldUpdateTexture.current;
      const { orientation } = shouldUpdateTexture.current;
      updateTexture(pieceUUID, orientation);
      shouldUpdateTexture.current = null;
    }
  }, [patterns]);

  useEffect(() => {
    setValue('texture', true);
  }, []);

  const menuPaterns = useMemo(() => {
    const sortedLabels = PatternsLabels.sort((a, b) => {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });

    const filteredLabels = sortedLabels.filter((el) => el.key !== 'TMP');
    return filteredLabels;
  }, []);

  return (
    <>
      {orientation.key === 'free' ? null : (
        <KanvasEditor
          orbitRef={orbitRef}
          kanvasSize={kanvasSize}
          orientation={orientation}
          setPatterns={setPatterns}
          patterns={patterns}
          pieces={pieces}
          pieceIndex={pieceIndex}
          selectedPattern={selectedPattern}
          setSelectedPattern={setSelectedPattern}
          stageRef={stageRef}
          customSteles={customSteles}
          customPlatings={customPlatings}
          shouldUpdateTexture={shouldUpdateTexture}
          isPatternsLoaded={isPatternsLoaded}
          setIsPatternsLoaded={setIsPatternsLoaded}
          customSoles={customSoles}
        />
      )}
      <Container>
        {selectedImportPage ? (
          <>
            <Row>
              <Typography
                variant='body2'
                color='#344054'
                style={{
                  marginLeft: '23px',
                  marginTop: '23px',
                  cursor: 'pointer',
                  zIndex: 2,
                }}
                onClick={() => setSelectedImportPage(undefined)}
              >
                {'Elements >'}
              </Typography>
              <Typography
                variant='body2'
                color='#344054'
                style={{
                  marginTop: '23px',
                  marginLeft: '4px',
                  fontWeight: 700,
                }}
              >
                {
                  PatternsLabels.find((el) => el.key === selectedImportPage)
                    ?.label
                }
              </Typography>
            </Row>
            {user ? (
              <Catalog
                setPatterns={setPatterns}
                patterns={patterns}
                pieceIndex={pieceIndex}
                pieces={pieces}
                setSelectedImportPage={setSelectedImportPage}
                setSelectedPiece={setSelectedPiece}
                orientation={orientation}
                selectedPage={selectedPage}
                user={user}
                selectedImportPage={selectedImportPage}
                orbitRef={orbitRef}
                customSteles={customSteles}
                customPlatings={customPlatings}
                customSoles={customSoles}
              />
            ) : null}
          </>
        ) : (
          <>
            <ViewSelectorContainer>
              <ViewRow>
                {/* Vue globale - à gauche */}
                <ViewButton
                  isSelected={orientation.key === 'free'}
                  disabled={isTexturesLoading()}
                  onClick={() => {
                    setSelectedPattern(undefined);
                    setOrientation({ key: 'free', inverse: false });
                  }}
                  style={{
                    minWidth: '100px',
                    alignSelf: pieceIndex !== -1 ? 'stretch' : 'auto',
                  }}
                >
                  <ViewIcon src='/faces/none.png' alt='Vue globale' />
                  <ViewLabel isSelected={orientation.key === 'free'}>
                    Vue globale
                  </ViewLabel>
                </ViewButton>

                {/* 6 faces en grille 3x2 à droite */}
                {pieceIndex !== -1 && (
                  <ViewGrid>
                    {VIEW_OPTIONS.filter((view) => view.key !== 'free').map(
                      (view) => {
                        const isSelected =
                          orientation.key === view.key &&
                          orientation.inverse === view.inverse;
                        return (
                          <Tooltip
                            key={`${view.key}-${view.inverse}`}
                            title={view.label}
                            arrow
                          >
                            <ViewButton
                              style={{
                                flexDirection: 'row',
                              }}
                              isSelected={isSelected}
                              disabled={isTexturesLoading()}
                              onClick={() => {
                                setSelectedPattern(undefined);
                                setOrientation({
                                  key: view.key,
                                  inverse: view.inverse,
                                });
                              }}
                            >
                              <ViewIcon src={view.icon} alt={view.label} />
                              <ViewLabel isSelected={isSelected}>
                                {view.label}
                              </ViewLabel>
                            </ViewButton>
                          </Tooltip>
                        );
                      },
                    )}
                  </ViewGrid>
                )}
              </ViewRow>
            </ViewSelectorContainer>
            {getPatterns()?.length > 0 ? (
              <MessageContainer
                style={{
                  height: 'fit-content',
                  padding: '22px',
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
              >
                <Typography
                  variant='body1'
                  color='#344054'
                  style={{
                    marginLeft: '4px',
                    fontWeight: 300,
                    fontSize: '14px',
                  }}
                >
                  {`Pour déplacer ${
                    selectedPage === 'patterns' ? ' un motif' : 'une gravure'
                  }, selectionnez une vue, cliquez sur le visuel à gauche ou sur le motif dans la
                  liste, maintenez le clic sur le visuel à gauche et bougez votre souris.`}
                </Typography>
              </MessageContainer>
            ) : null}
            <PiecesContainer>
              {getPatterns()?.length > 0 ? (
                getPatterns()?.map((el: any, elIndex: number) => {
                  if (
                    (selectedPage === 'patterns' && el.type !== 'image') ||
                    (selectedPage === 'engravings' && el.type !== 'text')
                  )
                    return <></>;
                  return selectedPage === 'patterns' ? (
                    <PatternItem
                      key={`pattern_${elIndex}`}
                      shouldUpdateTexture={shouldUpdateTexture}
                      selectedPattern={selectedPattern}
                      elIndex={elIndex}
                      updateKanvas={updateKanvas}
                      setSelectedPattern={setSelectedPattern}
                      el={el}
                      changePattern={changePattern}
                      applyPatternsTmp={applyPatternsTmp}
                      patterns={patterns}
                      setPatterns={setPatterns}
                      orientation={orientation}
                      setOrientation={setOrientation}
                    />
                  ) : (
                    <EngravingItem
                      key={`engraving${elIndex}`}
                      el={el}
                      selectedPattern={selectedPattern}
                      elIndex={elIndex}
                      setSelectedPattern={setSelectedPattern}
                      changeText={changeText}
                      patterns={patterns}
                      setPatterns={setPatterns}
                      orientation={orientation}
                      setOrientation={setOrientation}
                      shouldUpdateTexture={shouldUpdateTexture}
                    />
                  );
                })
              ) : (
                <MessageContainer>
                  <Typography
                    variant='body1'
                    color='#344054'
                    style={{
                      marginTop: '23px',
                      marginLeft: '4px',
                      fontWeight: 300,
                      fontSize: '14px',
                    }}
                  >
                    {`Cliquez sur une pièce puis choisissez une vue pour ajouter ${
                      selectedPage === 'patterns' ? 'un motif' : 'une gravure'
                    }`}
                  </Typography>
                </MessageContainer>
              )}
            </PiecesContainer>
          </>
        )}
      </Container>
      {orientation.key === 'free' ? null : (
        <PanelButtonContainer isOpen={isButtonOpen}>
          <PanelChoices
            isOpen={isButtonOpen}
            style={{
              flexDirection: PatternsLabels.length > 10 ? 'row' : 'column',
              flexWrap: 'wrap',
              width: PatternsLabels.length > 10 ? '100%' : '159px',
            }}
          >
            {selectedPage === 'patterns' ? (
              <>
                {menuPaterns.map((el) => {
                  return (
                    <PanelChoice
                      style={{
                        width: PatternsLabels.length > 10 ? '33%' : '99%',
                      }}
                      onClick={() => {
                        setSelectedImportPage(el.key);
                        setIsButtonOpen(false);
                      }}
                    >
                      <Typography
                        key={el.key}
                        variant='body2'
                        color='#344054'
                        style={{
                          fontSize: '12px',
                          userSelect: 'none',
                          textAlign: 'center',
                        }}
                      >
                        {el.label}
                      </Typography>
                    </PanelChoice>
                  );
                })}
              </>
            ) : null}
          </PanelChoices>
          {isButtonOpen ? (
            <>
              <PanelButton
                isOpen
                onClick={() => {
                  setSelectedImportPage('ALL');
                  setIsButtonOpen(false);
                }}
                style={{ marginBottom: '12px' }}
              >
                <Typography
                  variant='body2'
                  color='black'
                  style={{
                    fontSize: '12px',
                  }}
                >
                  Tous les motifs
                </Typography>
              </PanelButton>
              {user?.role === 'ADMIN' ? (
                <PanelButton
                  isOpen
                  onClick={() => {
                    setSelectedImportPage('TMP');
                    setIsButtonOpen(false);
                  }}
                  style={{ marginBottom: '12px' }}
                >
                  <Typography
                    variant='body2'
                    color='black'
                    style={{
                      fontSize: '12px',
                    }}
                  >
                    Motifs par défaut
                  </Typography>
                </PanelButton>
              ) : null}
            </>
          ) : null}

          {selectedPiece ? (
            <PanelButton isOpen={isButtonOpen} onClick={() => {}}>
              <Typography
                variant='body2'
                color={isButtonOpen ? 'black' : 'white'}
                style={{
                  fontSize: '12px',
                }}
              >
                Valider la sélection
              </Typography>
            </PanelButton>
          ) : (
            <PanelButton
              isOpen={isButtonOpen}
              onClick={() => {
                if (selectedPage === 'patterns') setIsButtonOpen(!isButtonOpen);
                if (selectedPage === 'engravings') addText();
              }}
            >
              <Typography
                variant='body2'
                color={isButtonOpen ? 'black' : 'white'}
                style={{
                  fontSize: '12px',
                }}
              >
                {isButtonOpen ? 'Annuler' : 'Ajouter un élément'}
              </Typography>
            </PanelButton>
          )}
        </PanelButtonContainer>
      )}
    </>
  );
};

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  top: 0,
});

const PanelButtonContainer = styled('div')(
  ({ isOpen }: { isOpen: boolean }) => ({
    position: 'absolute',
    width: '100%',
    height: '100%',

    padding: '27px',

    backgroundColor: `rgb(0, 0, 0, ${isOpen ? 0.4 : 0})`,
    transition: 'background-color 0.5s ease',

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    top: '0px',

    pointerEvents: 'none',
  }),
);

const PanelButton = styled('div')(({ isOpen }: { isOpen: boolean }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  width: '159px',
  height: '32px',

  backgroundColor: isOpen ? 'white' : '#BC9A67',
  transition: 'background-color 0.5s ease',

  borderRadius: '28px',
  padding: '8px 16px',

  cursor: 'pointer',
  zIndex: 2,
  pointerEvents: 'all',
}));

const PanelChoices = styled('div')(({ isOpen }: { isOpen: boolean }) => ({
  display: isOpen ? 'flex' : 'none',

  flexDirection: 'column',

  justifyContent: 'center',
  alignItems: 'flex-start',

  borderRadius: '10px',
  backgroundColor: 'white',

  width: '159px',

  marginBottom: '12px',

  '& > *:not(:last-child)': {
    borderBottom: '1px solid #98A2B3',
  },

  zIndex: 2,
  pointerEvents: 'all',
}));

const PanelChoice = styled('div')({
  padding: '10px 16px',
  width: '95%',

  cursor: 'pointer',
});

const Row = styled('div')({
  display: 'flex',
  flexDirection: 'row',
});

const PiecesContainer = styled('div')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '16px',
  padding: '16px 22px',
});

const MessageContainer = styled('div')({
  display: 'flex',
  width: '100%',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
});

const ViewSelectorContainer = styled('div')({
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',
});

const ViewRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  justifyContent: 'center',
});

const ViewGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '6px',
  flex: 1,
});

const ViewButton = styled('button')<{
  isSelected: boolean;
  disabled?: boolean;
}>(({ isSelected, disabled }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px 4px',
  border: isSelected ? '2px solid #BC9A67' : '1px solid #D0D5DD',
  borderRadius: '6px',
  backgroundColor: isSelected ? 'rgba(188, 154, 103, 0.1)' : 'white',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  transition: 'all 0.2s ease',
  minHeight: '44px',
  gap: '6px',

  '&:hover': {
    borderColor: disabled ? '#D0D5DD' : '#BC9A67',
    backgroundColor: disabled
      ? 'white'
      : isSelected
      ? 'rgba(188, 154, 103, 0.15)'
      : 'rgba(188, 154, 103, 0.05)',
  },
}));

const ViewIcon = styled('img')({
  width: '22px',
  height: '22px',
  objectFit: 'contain',
  marginBottom: '2px',
});

const ViewLabel = styled('span')<{ isSelected: boolean }>(({ isSelected }) => ({
  fontSize: '14px',
  fontWeight: isSelected ? 600 : 400,
  color: isSelected ? '#BC9A67' : '#344054',
  textAlign: 'center',
  lineHeight: 1.1,
}));
